import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { APIStatus, isAPIFetching } from '@flowwai-frontend/shared/api-common'

import {
   BACKSPACE_KEY_CODE,
   BACkSPACE_KEY_VALUE,
   DELETE_KEY_CODE,
   DELETE_KEY_VALUE,
   LEFT_ARROW_KEY_CODE,
   LEFT_ARROW_KEY_VALUE,
   RIGHT_ARROW_KEY_CODE,
   RIGHT_ARROW_KEY_VALUE,
   SPACE_KEY_VALUE,
   SPACE_SYMBOL_KEY_VALUE,
   SPACE_BAR_KEY_CODE,
   SPACE_BAR_KEY_VALUE,
   E_KEY_VALUE,
   E_KEY_CODE
} from '@flowwai-frontend/shared/constants'
import { checkIsNumber } from '@flowwai-frontend/shared/utils/common'
import { MinusIcon } from '@flowwai-frontend/design-system/icons'

import SingleOTPInput, { isStyleObject } from '../SingleOTPInput/SingleOTPInput'
import * as Styles from './styles'

interface OtpInputProps {
   placeholder?: string | null
   containerStyle: any
   separator?: any
   inputStyle: any
   focusStyle: any
   isDisabled?: boolean
   disabledStyle?: any
   hasErrored?: boolean
   errorStyle: any
   shouldAutoFocus: boolean
   isInputNum?: boolean
   isInputSecure?: boolean
   className?: string
   onBlur?: (event: React.SyntheticEvent) => void
   isFocus?: boolean
   numInputs: number
   onChange: (otp: string) => void
   value: string
   apiStatus?: APIStatus
   showToast?: () => void
   hideSeparator?: boolean
}

const OTPInputBoxes = (props: OtpInputProps): ReactElement => {
   const { numInputs = 6, value = '' } = props

   const [activeInput, setActiveInput] = useState(0)

   const getOtpValue = (): string[] => (value ? value.toString().split('') : [])

   const getPlaceholderValue = (): string | null => {
      const { placeholder, numInputs } = props

      if (typeof placeholder === 'string') {
         if (placeholder.length === numInputs) {
            return placeholder
         }

         if (placeholder.length > 0) {
            console.error(
               'Length of the placeholder should be equal to the number of inputs.'
            )
         }
      }
      return null
   }

   useEffect(() => {
      if (isAPIFetching(props.apiStatus)) {
         setActiveInput(-1)
      } else {
         if (value && value.length) {
            if (value.length === numInputs) {
               setActiveInput(value.length - 1)
            } else {
               setActiveInput(value.length)
            }
         } else {
            setActiveInput(0)
         }
      }
   }, [props.apiStatus])

   // Helper to return OTP from input
   const handleOtpChange = (otp: string[]): void => {
      const { onChange } = props

      const otpValue = otp.join('')

      onChange(otpValue)
   }

   const isInputValueValid = (value: string): boolean => {
      const isTypeValid = props.isInputNum
         ? !isNaN(parseInt(value, 10))
         : typeof value === 'string'

      return isTypeValid && value.trim().length === 1
   }

   // Focus on input by index
   const focusInput = (input: number): void => {
      const { numInputs } = props

      const activeInput = Math.max(Math.min(numInputs - 1, input), 0)
      setActiveInput(activeInput)
   }

   // Focus on next input
   const focusNextInput = (): void => {
      focusInput(activeInput + 1)
   }

   // Focus on previous input
   const focusPrevInput = (): void => {
      focusInput(activeInput - 1)
   }

   // Change OTP value at focused input
   const changeCodeAtFocus = (value: string): void => {
      const otp = getOtpValue()
      otp[activeInput] = value[0]
      handleOtpChange(otp)
   }

   // Handle pasted OTP
   const handleOnPaste = (event: React.ClipboardEvent): void => {
      event.preventDefault()
      const { numInputs, isDisabled } = props

      if (isDisabled) {
         return
      }

      const otp = getOtpValue()
      let nextActiveInput = activeInput

      // Get pastedData in an array of max size (num of inputs - current position)
      const pastedData = event.clipboardData
         .getData('text/plain')
         .slice(0, numInputs - activeInput)
         .split('')
      if (!checkIsNumber(pastedData.join(''))) {
         return
      }
      // Paste data from focused input onwards
      for (let pos = 0; pos < numInputs; ++pos) {
         if (pos >= activeInput && pastedData.length > 0) {
            const data = pastedData.shift()
            if (data) {
               otp[pos] = data
               nextActiveInput++
            }
         }
      }

      setActiveInput(nextActiveInput)
      focusInput(nextActiveInput)
      handleOtpChange(otp)
   }

   const handleOnChange = (
      event: React.ChangeEvent<HTMLInputElement>
   ): void => {
      const { value } = event.target
      changeCodeAtFocus(value)
   }

   // Handle cases of backspace, delete, left arrow, right arrow, space
   const handleOnKeyDown = (event: React.KeyboardEvent): void => {
      const newLocal =
         event.keyCode === BACKSPACE_KEY_CODE ||
         event.key === BACkSPACE_KEY_VALUE
      if (newLocal) {
         event.preventDefault()
         changeCodeAtFocus('')
         focusPrevInput()
      } else if (
         event.keyCode === DELETE_KEY_CODE ||
         event.key === DELETE_KEY_VALUE
      ) {
         event.preventDefault()
         changeCodeAtFocus('')
      } else if (
         event.keyCode === LEFT_ARROW_KEY_CODE ||
         event.key === LEFT_ARROW_KEY_VALUE
      ) {
         event.preventDefault()
         focusPrevInput()
      } else if (
         event.keyCode === RIGHT_ARROW_KEY_CODE ||
         event.key === RIGHT_ARROW_KEY_VALUE
      ) {
         event.preventDefault()
         if (value[activeInput + 1]) focusNextInput()
      } else if (
         event.keyCode === SPACE_BAR_KEY_CODE ||
         event.key === SPACE_SYMBOL_KEY_VALUE ||
         event.key === SPACE_BAR_KEY_VALUE ||
         event.key === SPACE_KEY_VALUE ||
         event.keyCode === E_KEY_CODE ||
         event.key === E_KEY_VALUE
      ) {
         event.preventDefault()
      }
   }

   // The content may not have changed, but some input took place hence change the focus
   const handleOnInput = (event: any): void => {
      //FIXME:Add Appropriate type
      if (isInputValueValid(event.target.value)) {
         focusNextInput()
      } else {
         // This is a workaround for dealing with keyCode "229 Unidentified" on Android.

         if (!props.isInputNum) {
            const { nativeEvent } = event

            if (
               nativeEvent.data === null &&
               nativeEvent.inputType === 'deleteContentBackward'
            ) {
               event.preventDefault()
               changeCodeAtFocus('')
               focusPrevInput()
            }
         }
      }
   }

   const onFocus = (
      event: React.FocusEvent<HTMLInputElement>,
      index: number
   ): void => {
      setActiveInput(index)
      event.target.select()
   }

   const renderInputs = () => {
      const {
         numInputs,
         inputStyle,
         focusStyle,
         separator,
         isDisabled,
         disabledStyle,
         hasErrored,
         errorStyle,
         shouldAutoFocus,
         isInputNum,
         isInputSecure,
         className,
         onBlur,
         isFocus
      } = props
      const inputs: any = []
      const otp = getOtpValue()
      const placeholder = getPlaceholderValue()

      for (let i = 0; i < numInputs; i++) {
         const isLastInput = i === numInputs - 1

         const isMiddleInput = i === Math.round(numInputs / 2) - 1

         inputs.push(
            <div
               key={i}
               style={{
                  marginRight: isLastInput ? '0px' : '12px',
                  display: 'flex',
                  alignItems: 'center'
               }}
            >
               <SingleOTPInput
                  placeholder={placeholder?.[i]}
                  key={i}
                  index={i}
                  disabled={activeInput < i && !otp[i]}
                  focus={activeInput === i}
                  value={otp?.[i]}
                  onChange={handleOnChange}
                  onKeyDown={handleOnKeyDown}
                  onInput={handleOnInput}
                  onPaste={handleOnPaste}
                  onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
                     onFocus(event, i)
                  }
                  separator={separator}
                  inputStyle={inputStyle}
                  focusStyle={focusStyle}
                  isLastChild={i === numInputs - 1}
                  isDisabled={isDisabled}
                  disabledStyle={disabledStyle}
                  hasErrored={hasErrored}
                  errorStyle={errorStyle}
                  shouldAutoFocus={shouldAutoFocus}
                  isInputNum={isInputNum}
                  isInputSecure={isInputSecure}
                  className={className}
                  onBlur={onBlur}
                  isFocus={isFocus}
                  activeInput={activeInput}
                  otp={otp}
               />
               {isMiddleInput && !props.hideSeparator ? (
                  <div className={cn(Styles.minusIconStyles)}>
                     <MinusIcon />
                  </div>
               ) : null}
            </div>
         )
      }

      return inputs
   }

   const { containerStyle } = props

   return (
      <div
         style={Object.assign(
            { display: 'flex' },
            isStyleObject(containerStyle) && containerStyle
         )}
         className={!isStyleObject(containerStyle) ? containerStyle : ''}
      >
         {renderInputs()}
      </div>
   )
}

export default observer(OTPInputBoxes)
